/**
 * Tooltips: the shared state (which one is open, whether this is a touch
 * device) and the touch detection behind it.
 *
 * Touch matters because a tooltip that opens on hover never opens on a
 * phone; TooltipContext reads isActualTouchDevice to open them on tap there.
 *
 * Who uses it: TooltipContext (isActualTouchDevice). useTooltip itself has
 * no caller today, and nothing else reads TooltipContext, so the
 * TooltipProvider TemperatureForecast mounts provides to no one.
 *
 * @module useTooltip
 */

import { useContext } from 'react';
import { TooltipContext, TooltipContextType } from '../context/tooltipContextDefinition';
import { debugLog } from '@/config/config';

/**
 * Custom hook for accessing tooltip context and functionality
 * 
 * Provides access to:
 * - Device detection state (mobile/touch)
 * - Active tooltip management
 * - Theme information
 * 
 * @throws {Error} When used outside of TooltipProvider
 * @returns {TooltipContextType} Tooltip context values and functions
 * 
 * @example
 * ```tsx
 * const { isMobile, isTouch, activeTooltipId, setActiveTooltipId, theme } = useTooltip();
 * 
 * // Handle tooltip activation
 * const handleTooltipOpen = () => setActiveTooltipId('my-tooltip');
 * ```
 */
export function useTooltip(): TooltipContextType {
  const context = useContext(TooltipContext);
  
  if (context === undefined) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }
  
  return context;
}

/**
 * Utility function to detect if the device is a touch device
 * 
 * Detection is based on:
 * 1. User agent string analysis
 * 2. Media query for hover capability
 * 3. Touch points check
 * 
 * @returns {boolean} True if the device is determined to be a touch device
 * 
 * @example
 * ```ts
 * const isTouch = isActualTouchDevice();
 * debugLog('Tooltip', 'Is touch device:', isTouch);
 * ```
 */
export function isActualTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;

  const hasTouch = 'ontouchstart' in window || 
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - vendor prefixed property
    navigator.msMaxTouchPoints > 0;

  const hasHover = window.matchMedia('(hover: hover)').matches;
  const isMobileUA = /Mobi|Android|iPhone|iPad|Windows Phone/i.test(navigator.userAgent);

  const result = hasTouch && (!hasHover || isMobileUA);

  debugLog('Tooltip', '[isActualTouchDevice] Detection results:', {
    hasTouch,
    hasHover,
    isMobileUA,
    result
  });

  return result;
} 